import { Box, Typography } from '@mui/material';
import { useAnalyzeAudience } from 'client/modules/Analyze/AnalyzeAudienceContext';
import {
  cardSx,
  groupColor,
  INK,
  MUTED,
  scoreColor,
  SECONDARY,
  sectionTitleSx,
  tableHeadCellSx,
} from 'client/modules/Analyze/analyzeTheme';
import AnalyzeAudienceControls from 'client/modules/Analyze/components/AnalyzeAudienceControls';
import ScoreGroupsDonut from 'client/modules/Analyze/components/ScoreGroupsDonut';
import groupsByType from 'client/modules/Surveys/pages/DashboardPage/groupsByType';
import { useGetSnapshotQuery } from 'client/modules/Surveys/surveyAnalyzeApiSlice';
import ActivityIndicator from 'client/modules/App/components/ActivityIndicator/ActivityIndicator';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useOutletContext } from 'react-router-dom';

const messages = defineMessages({
  groupsTitle: { id: 'analyze.snapshot.groupsTitle', defaultMessage: 'Score groups' },
  scoresTitle: { id: 'analyze.snapshot.scoresTitle', defaultMessage: 'Score distribution' },
  breakdownTitle: { id: 'analyze.snapshot.breakdownTitle', defaultMessage: 'Score breakdown' },
  colScore: { id: 'analyze.snapshot.colScore', defaultMessage: 'Score' },
  colResponses: { id: 'analyze.snapshot.colResponses', defaultMessage: 'Responses' },
  colShare: { id: 'analyze.snapshot.colShare', defaultMessage: 'Share' },
  responses: { id: 'analyze.snapshot.responses', defaultMessage: '{count} responses' },
  comments: {
    id: 'analyze.snapshot.comments',
    defaultMessage: '{count} comments ({percent}%)',
  },
  loadError: { id: 'analyze.snapshot.loadError', defaultMessage: 'Failed to load snapshot' },
  noData: { id: 'analyze.snapshot.noData', defaultMessage: 'No responses in this period' },
  promoters: { id: 'Dashboard.Tooltip.Promoters', defaultMessage: 'promoters' },
  passives: { id: 'Dashboard.Tooltip.Passives', defaultMessage: 'passives' },
  detractors: { id: 'Dashboard.Tooltip.Detractors', defaultMessage: 'detractors' },
  agree: { id: 'Dashboard.Tooltip.Agree', defaultMessage: 'agree' },
  neutral: { id: 'Dashboard.Tooltip.Neutral', defaultMessage: 'neutral' },
  disagree: { id: 'Dashboard.Tooltip.Disagree', defaultMessage: 'disagree' },
  satisfied: { id: 'Dashboard.Tooltip.Satisfied', defaultMessage: 'satisfied' },
  dissatisfied: { id: 'Dashboard.Tooltip.Dissatisfied', defaultMessage: 'dissatisfied' },
  stars: {
    id: 'Dashboard.Tooltip.Stars',
    defaultMessage: '{num, number}-{num, plural, one {star} other {stars}}',
  },
});

const groupMessageKeys = {
  promoters: 'promoters',
  passives: 'passives',
  detractors: 'detractors',
  agree: 'agree',
  neutral: 'neutral',
  disagree: 'disagree',
  satisfied: 'satisfied',
  dissatisfied: 'dissatisfied',
};

function AnalyzeSnapshotPage() {
  const { survey: outletSurvey } = useOutletContext();
  const { survey, audienceParams } = useAnalyzeAudience();
  const activeSurvey = survey || outletSurvey;
  const { formatMessage, formatNumber } = useIntl();

  const query = useMemo(
    () => ({ survey: activeSurvey?.id, ...audienceParams }),
    [activeSurvey?.id, audienceParams],
  );

  const { data, isFetching, isError } = useGetSnapshotQuery(query, { skip: !activeSurvey?.id });

  if (!activeSurvey) {
    return null;
  }

  const surveyType = data?.surveyType || activeSurvey.surveyType || 'nps';
  const groups = groupsByType[surveyType] || groupsByType.nps;
  const totalResponses = data?.responses ?? 0;
  const maxScoreCount = Math.max(...(data?.scores?.map(s => s.count) || [0]), 1);

  const formatGroupLabel = group => {
    if (surveyType === 'star_five') {
      const [num] = group.split('-');
      return formatMessage(messages.stars, { num: Number(num) });
    }
    const key = groupMessageKeys[group];
    return key ? formatMessage(messages[key]) : group;
  };

  const formatBarCount = count => {
    if (count >= 1000) {
      return formatNumber(count, { notation: 'compact', maximumFractionDigits: 1 });
    }
    return formatNumber(count);
  };

  const scoreLabel = surveyType === 'nps' ? 'NPS' : surveyType.toUpperCase();
  const displayScore =
    data?.nps != null
      ? data.nps
      : surveyType === 'star_five' && data?.scores?.length
        ? Math.round(
            data.scores.reduce((sum, { score, count }) => sum + score * count, 0) /
              Math.max(totalResponses, 1),
          )
        : null;

  const pctOf = count =>
    totalResponses > 0 ? Math.round((count / totalResponses) * 1000) / 10 : 0;

  return (
    <Box>
      <AnalyzeAudienceControls />
      {isFetching && !data && <ActivityIndicator />}
      {isError && !data && (
        <Typography color="error">{formatMessage(messages.loadError)}</Typography>
      )}
      {data && totalResponses === 0 && !isFetching && (
        <Typography>{formatMessage(messages.noData)}</Typography>
      )}
      {data && totalResponses > 0 && (
        <>
          <Typography sx={{ fontSize: 14, color: SECONDARY, mb: 2 }}>
            {formatMessage(messages.responses, { count: formatNumber(totalResponses) })}
            {data.comments?.count > 0 &&
              ` · ${formatMessage(messages.comments, {
                count: formatNumber(data.comments.count),
                percent: formatNumber(data.comments.percent ?? 0),
              })}`}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1.55fr' },
              gap: 2.25,
              mb: 2.25,
            }}
          >
            {/* Score groups donut */}
            <Box sx={cardSx}>
              <Typography sx={sectionTitleSx}>{formatMessage(messages.groupsTitle)}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
                <ScoreGroupsDonut
                  groups={data.groups}
                  order={groups}
                  total={totalResponses}
                  scoreLabel={scoreLabel}
                  displayScore={displayScore}
                  formatNumber={formatNumber}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {groups.map(group => {
                    const count = data.groups?.[group] ?? 0;
                    if (!count) {
                      return null;
                    }
                    return (
                      <Box key={group}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 11,
                              height: 11,
                              borderRadius: '3px',
                              bgcolor: groupColor(group),
                            }}
                          />
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: INK }}>
                            {formatGroupLabel(group)}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 14, color: MUTED, ml: '19px' }}>
                          {formatNumber(pctOf(count))}% · {formatNumber(count)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* Score distribution histogram */}
            {data.scores?.length > 0 && (
              <Box sx={cardSx}>
                <Typography sx={{ ...sectionTitleSx, mb: 2 }}>
                  {formatMessage(messages.scoresTitle)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.25, height: 230 }}>
                  {data.scores.map(({ score, count }) => {
                    const heightPct = Math.max(2, Math.round((count / maxScoreCount) * 100));
                    return (
                      <Box
                        key={score}
                        sx={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          height: '100%',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: SECONDARY,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {count > 0 ? `${formatNumber(pctOf(count))}%` : ' '}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#A6AFBD', mb: 0.75 }}>
                          {count > 0 ? formatBarCount(count) : ' '}
                        </Typography>
                        <Box
                          sx={{
                            width: '100%',
                            borderRadius: '5px 5px 0 0',
                            bgcolor: scoreColor(score, surveyType),
                            height: `${heightPct}%`,
                            minHeight: count > 0 ? 4 : 0,
                          }}
                          title={`${score}: ${formatNumber(count)} (${formatNumber(pctOf(count))}%)`}
                        />
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: SECONDARY, mt: 1 }}>
                          {score}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>

          {/* Score breakdown table */}
          {data.scores?.length > 0 && (
            <Box sx={cardSx}>
              <Typography sx={{ ...sectionTitleSx, mb: 2 }}>
                {formatMessage(messages.breakdownTitle)}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: '0 16px',
                  pb: 1.25,
                  borderBottom: '1px solid #ECEFF3',
                  ...tableHeadCellSx,
                }}
              >
                <span>{formatMessage(messages.colScore)}</span>
                <span style={{ textAlign: 'right' }}>{formatMessage(messages.colResponses)}</span>
                <span style={{ textAlign: 'right' }}>{formatMessage(messages.colShare)}</span>
              </Box>
              {data.scores.map(({ score, count }) => (
                <Box
                  key={score}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    gap: '0 16px',
                    alignItems: 'center',
                    fontSize: 14,
                    color: INK,
                    py: 1.1,
                    borderBottom: '1px solid #F4F6F9',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        bgcolor: scoreColor(score, surveyType),
                      }}
                    />
                    <b style={{ fontVariantNumeric: 'tabular-nums' }}>{score}</b>
                  </Box>
                  <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(count)}
                  </Box>
                  <Box
                    sx={{
                      textAlign: 'right',
                      color: MUTED,
                      fontVariantNumeric: 'tabular-nums',
                      width: 52,
                    }}
                  >
                    {formatNumber(pctOf(count))}%
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default AnalyzeSnapshotPage;
